import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useTrackDeletedItems } from "@air/analytics";
import { Assets } from "@air/api";
import { useToasts } from "@air/provider-toast";
import { useQueryClient } from "@tanstack/react-query";
import Link from "next/link";
import pluralize from "pluralize";
import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { Routes } from "~/constants/routes";
import { useFeatureFlags } from "~/hooks/useFeatureFlags";
import { useCurrentWorkspace } from "~/providers/CurrentWorkspaceProvider";
import { deletedClipsSuccessAction } from "~/store/centralizedClip/actions";
import { getWorkspaceStorageKey } from "~/swr-hooks/subscriptions/useStorage";
import { reportErrorToBugsnag } from "~/utils/ErrorUtils";
import { useUpdateBoardStats } from "~/utils/mutateUtils/BoardStats";
import { useRemoveAssetsFromAllViews } from "~/utils/mutateUtils/GalleryAssets";
export const useDeletePrivateClips = ()=>{
    const dispatch = useDispatch();
    const { currentWorkspace } = useCurrentWorkspace();
    const { removeAssetsFromAllViews } = useRemoveAssetsFromAllViews();
    const { updateBoardStats } = useUpdateBoardStats();
    const queryClient = useQueryClient();
    const { trackDeletedItems } = useTrackDeletedItems();
    const { showToast } = useToasts();
    const { deletionRecovery: deletionRecoveryFlag } = useFeatureFlags();
    const deletePrivateClips = useCallback(async (param)=>{
        let { assetIds, parentBoardId, trackLocation } = param;
        try {
            if (!(currentWorkspace === null || currentWorkspace === void 0 ? void 0 : currentWorkspace.id)) {
                throw new Error("No current workspace id");
            }
            const response = await Assets.batchDelete({
                assetIds,
                workspaceId: currentWorkspace.id
            });
            if (response.deletedAssetIds.length > 0) {
                showToast(deletionRecoveryFlag ? /*#__PURE__*/ _jsxs("span", {
                    children: [
                        pluralize("asset", response.deletedAssetIds.length, true),
                        " moved to",
                        " ",
                        /*#__PURE__*/ _jsx(Link, {
                            href: Routes.media.recentlyDeleted,
                            className: "cursor-pointer text-14 text-blue-7",
                            children: "Recently Deleted"
                        })
                    ]
                }) : "Deleted ".concat(pluralize("asset", response.deletedAssetIds.length, true)));
            }
            if (response.deletedAssetIds.length > 0 || response.removedAssetIds.length > 0) {
                const mergedAssetIds = [
                    ...response.deletedAssetIds || [],
                    ...response.removedAssetIds || []
                ];
                trackDeletedItems({
                    item_ids: mergedAssetIds,
                    item_total: mergedAssetIds.length,
                    item_type: "asset",
                    location: trackLocation
                });
                dispatch(deletedClipsSuccessAction({
                    assetIds: mergedAssetIds
                }));
                if (!!currentWorkspace) {
                    queryClient.invalidateQueries({
                        queryKey: getWorkspaceStorageKey(currentWorkspace === null || currentWorkspace === void 0 ? void 0 : currentWorkspace.id)
                    });
                }
                removeAssetsFromAllViews({
                    assetIds: mergedAssetIds
                });
                if (parentBoardId) {
                    updateBoardStats(parentBoardId);
                }
            }
            return response;
        } catch (error) {
            throw reportErrorToBugsnag({
                error,
                context: "Failed to delete clips",
                metadata: {
                    Data: {
                        assetIds,
                        trackLocation
                    }
                }
            });
        }
    }, [
        currentWorkspace,
        showToast,
        deletionRecoveryFlag,
        trackDeletedItems,
        removeAssetsFromAllViews,
        dispatch,
        queryClient,
        updateBoardStats
    ]);
    return {
        deletePrivateClips
    };
};
